<script>
  export let question;
  export let updateWorkingQuestion;
  
  // Get available questions for reference
  function getAvailableQuestions() {
    const currentQuestionIndex = parseInt(question.id?.split('--')[1]) || 0;
    const questions = [];
    
    for (let i = 0; i < currentQuestionIndex; i++) {
      questions.push({
        index: i,
        id: `question--${i}`,
        label: `Question ${i}`,
        value: `__answers${i}__`
      });
    }
    
    return questions;
  }
  
  function toggleManyToMany() {
    if (question.manyToMany) {
      // Remove manyToMany and related properties
      updateWorkingQuestion('manyToMany', undefined);
      updateWorkingQuestion('fieldLabels', undefined);
      updateWorkingQuestion('startingFields', undefined);
      updateWorkingQuestion('maxAnswers', undefined);
      updateWorkingQuestion('extendable', undefined);
    } else {
      // Add manyToMany and set defaults
      updateWorkingQuestion('manyToMany', true);
      updateWorkingQuestion('fieldLabels', '__priorAnswers__');
      updateWorkingQuestion('startingFields', 5);
      updateWorkingQuestion('maxAnswers', 10);
      updateWorkingQuestion('extendable', true);
    }
  }
  
  function updateFieldLabelsReference(value) {
    updateWorkingQuestion('fieldLabels', value);
  }
  
  function canBeManyToMany() {
    // Check if this question can be manyToMany
    const hasNodes = question.dataset?.some(handler => handler[0] === 'nodes');
    const hasSelfNode = question.dataset?.some(handler => handler[0] === 'setSelf');
    const hasSelfProps = question.dataset?.some(handler => handler[0] === 'selfProps');
    const hasNodeProps = question.dataset?.some(handler => handler[0] === 'nodeProps');
    
    // Can be manyToMany if it creates nodes OR is a props question, but NOT if it's a selfNode
    return (hasNodes || hasSelfProps || hasNodeProps) && !hasSelfNode;
  }
  
  // Reactive derived state
  $: availableQuestions = getAvailableQuestions();
  $: isManyToMany = question.manyToMany === true;
  $: canUseManyToMany = canBeManyToMany();
</script>

<div class="edit-section" class:disabled={!canUseManyToMany}>
  <h4>
    Many-to-Many Configuration
    {#if isManyToMany}
      <span class="many-badge">MANY-TO-MANY</span>
    {/if}
  </h4>
  
  <div class="form-styled">
    {#if !canUseManyToMany}
      <div class="cannot-use-info">
        <p>❌ Many-to-Many is not available for this question type</p>
        <p class="restriction-text">
          Many-to-Many can be used with questions that create nodes or add properties to nodes, but not with selfNode questions
        </p>
      </div>
    {:else}
      <div class="field-wrapper">
        <label class="checkbox-item">
          <input 
            type="checkbox"
            checked={isManyToMany}
            on:change={toggleManyToMany}
          />
          Enable Many-to-Many
        </label>
        <p class="field-help">
          Create multiple nodes connected to each answer from another question
        </p>
      </div>
      
      {#if isManyToMany}
        <div class="many-to-many-config">
          
          <!-- Field Labels Reference -->
          <div class="field-wrapper">
            <label>Connect to Question</label>
            <select 
              value={question.fieldLabels || '__priorAnswers__'}
              on:change={(e) => updateFieldLabelsReference(e.target.value)}
              class="question-select"
            >
              <option value="__priorAnswers__">Previous Question (All Answers)</option>
              <option value="__priorAnswer__">Previous Question (Single Answer)</option>
              {#each availableQuestions as q}
                <option value="__answers{q.index}__">Question {q.index} (All Answers)</option>
                <option value="__answer{q.index}__">Question {q.index} (Single Answer)</option>
              {/each}
            </select>
            <p class="field-help">
              Choose which question's answers to connect to
            </p>
          </div>
          
          <!-- Starting Fields -->
          <div class="field-wrapper">
            <label>Starting Fields per Answer</label>
            <input 
              type="number"
              value={question.startingFields || 5}
              min="1"
              max="50"
              on:input={(e) => updateWorkingQuestion('startingFields', parseInt(e.target.value) || 5)}
            />
            <p class="field-help">
              Number of input fields to show initially for each connected answer
            </p>
          </div>
          
          <!-- Extendable Settings -->
          <div class="field-wrapper">
            <label class="checkbox-item">
              <input 
                type="checkbox"
                checked={question.extendable === true}
                on:change={(e) => updateWorkingQuestion('extendable', e.target.checked)}
              />
              Allow adding more fields
            </label>
            <p class="field-help">
              Let users add more input fields beyond the starting amount
            </p>
          </div>
          
          {#if question.extendable}
            <div class="field-wrapper">
              <label>Maximum Fields per Answer</label>
              <input 
                type="number"
                value={question.maxAnswers || 10}
                min={question.startingFields || 5}
                max="100"
                on:input={(e) => updateWorkingQuestion('maxAnswers', parseInt(e.target.value) || 10)}
              />
              <p class="field-help">
                Maximum number of fields users can add for each connected answer
              </p>
            </div>
          {/if}
          
          <!-- Info Box -->
          <div class="many-to-many-info">
            <h6>Many-to-Many Summary</h6>
            <ul>
              <li>Connected to: <code>{question.fieldLabels || '__priorAnswers__'}</code></li>
              <li>Starting fields: <strong>{question.startingFields || 5}</strong> per answer</li>
              <li>Extendable: <strong>{question.extendable ? 'Yes' : 'No'}</strong></li>
              {#if question.extendable}
                <li>Max fields: <strong>{question.maxAnswers || 10}</strong> per answer</li>
              {/if}
            </ul>
          </div>
        </div>
      {/if}
    {/if}
  </div>
</div>

<style lang="scss">
  .edit-section {
    border: 4px solid #d4d3bc;
    border-radius: 0;
    padding: 15px;
    margin-bottom: 20px;
    background: #f3f2e3;
    
    &.disabled {
      opacity: 0.7;
      background: #f9f9f9;
    }
    
    h4 {
      margin-bottom: 15px;
      font-weight: bold;
      font-size: 0.9rem;
      display: flex;
      align-items: center;
      gap: 8px;
    }
  }
  
  .many-badge {
    background: #8b5cf6;
    color: white;
    padding: 2px 8px;
    border-radius: 12px;
    font-size: 10px;
    font-weight: bold;
    letter-spacing: 0.5px;
  }
  
  .cannot-use-info {
    text-align: center;
    padding: 20px;
    border: 2px dashed #f87171;
    border-radius: 6px;
    background: #fef2f2;
    
    p {
      margin: 8px 0;
      color: #dc2626;
      font-weight: 500;
      
      &.restriction-text {
        font-size: 0.8rem;
        font-style: italic;
        font-weight: normal;
      }
    }
  }
  
  .many-to-many-config {
    background: #f3e8ff;
    border: 2px solid #8b5cf6;
    border-radius: 6px;
    padding: 15px;
    margin-top: 15px;
  }
  
  .field-wrapper {
    margin-bottom: 15px;
    
    label {
      display: inline-flex;
      font-weight: bold;
      margin-bottom: 5px;
      font-size: 0.75rem;
      color: #464b4b;
    }
    
    input, select {
      width: 100%;
      padding: 8px 12px;
      border: 4px solid #d4d3bc;
      border-radius: 0;
      background: #f3f2e3;
      font-size: 0.75rem;
      
      &:focus {
        outline: none;
        border-color: #8b5cf6;
        background: #fff;
      }
    }
  }
  
  .question-select {
    font-family: monospace;
    font-size: 0.7rem;
  }
  
  .checkbox-item {
    display: flex;
    align-items: center;
    cursor: pointer;
    font-size: 0.75rem;
    
    input {
      margin-right: 8px;
      width: auto;
    }
  }
  
  .many-to-many-info {
    background: #ede9fe;
    border: 1px solid #c4b5fd;
    border-radius: 6px;
    padding: 12px;
    margin-top: 15px;
    
    h6 {
      margin: 0 0 8px 0;
      font-size: 0.75rem;
      font-weight: bold;
      color: #6b21a8;
    }
    
    ul {
      margin: 0;
      padding-left: 15px;
      
      li {
        font-size: 0.7rem;
        margin-bottom: 4px;
        color: #7c3aed;
        
        code {
          background: #f3e8ff;
          padding: 1px 4px;
          border-radius: 2px;
          font-family: monospace;
          color: #6b21a8;
        }
      }
    }
  }
  
  .field-help {
    font-size: 11px;
    color: #6b7280;
    margin-top: 5px;
    font-style: italic;
  }
</style>